import React, { useCallback, useEffect, useRef, useState } from "react";
import { EonUiIcon } from "@eon-ui/eon-ui-components-react";
import { useJwtToken, useUserData } from "@me8eon/react_authentication";
import { useTranslation } from "@me8eon/translation";
import { SelectedCategory, fmt } from "../poData";
import IncidentCategoryPanel from "../components/IncidentCategoryPanel";
import TabSkeleton from "../components/TabSkeleton";
import TabError from "../components/TabError";
import { DashboardRange, IncidentCategory, fetchIncidentCategoryMatches } from "../../../services/dashboardApi";
import { useDashboardData } from "../useDashboardData";

// The search bar's match-count call is debounced independently of (and never mixed into) the
// main incident-categories/KPI fetch above — it only ever updates the `matches` badge state below.
const MATCH_DEBOUNCE_MS = 300;

type Props = {
    range: DashboardRange;
    periodDays: number;
    isCustomPeriod: boolean;
    categoryFilter: SelectedCategory[];
    companyFilter: string[];
};

const IncidentsTab: React.FC<Props> = ({ range, periodDays, isCustomPeriod, categoryFilter, companyFilter }) => {
    const t = useTranslation();
    const tp = (key: string) => t(`productDashboard.incidents.${key}`);
    const tc = (key: string) => t(`productDashboard.common.${key}`);
    // Real MyHelp incidents (Snowflake → Postgres), windowed + category/company-filtered via the shared hook.
    const { kpis, incidentCategories, loading, error } = useDashboardData(range, categoryFilter, companyFilter);
    const maxIncidents = Math.max(1, ...incidentCategories.map((c) => c.incidents));
    const [selectedCat, setSelectedCat] = useState<SelectedCategory | null>(null);
    const [searchValue, setSearchValue] = useState("");
    const searchTerm = searchValue.trim();
    // Open the drill-down popup for a category (only live rows carry parent/child).
    const openCategory = (c: IncidentCategory) => {
        if (c.parent && c.child) setSelectedCat({ parent: c.parent, child: c.child, label: c.category });
    };

    // Search-only match counts per category ("N matches" badge) — a separate, debounced call that
    // never touches `incidentCategories`/`kpis` above; keyed by "parent/child".
    const idToken = useJwtToken();
    const { loggedIn } = useUserData();
    const [matches, setMatches] = useState<Record<string, number>>({});
    const rangeKey = "from" in range ? `r:${range.from}:${range.to}` : `w:${range.window}`;
    const categoriesKey = categoryFilter.map((c) => c.child).join(",");
    const companiesKey = companyFilter.join(",");
    const matchRequestIdRef = useRef(0);
    const latestMatchRef = useRef({ range, idToken, categoryFilter, companyFilter, loggedIn });
    latestMatchRef.current = { range, idToken, categoryFilter, companyFilter, loggedIn };

    const runSearchMatches = useCallback((term: string) => {
        const { range, idToken, categoryFilter, companyFilter, loggedIn } = latestMatchRef.current;
        if (!loggedIn) return;
        const requestId = ++matchRequestIdRef.current;
        const cats = categoryFilter.map((c) => c.child);
        const filters = { categories: cats.length ? cats : undefined, companies: companyFilter.length ? companyFilter : undefined };
        fetchIncidentCategoryMatches(range, term, idToken, filters)
            .then((list) => {
                if (matchRequestIdRef.current !== requestId) return; // superseded
                const map: Record<string, number> = {};
                list.forEach((m) => { if (m.parent && m.child) map[`${m.parent}/${m.child}`] = m.matches; });
                setMatches(map);
            })
            .catch(() => { if (matchRequestIdRef.current === requestId) setMatches({}); });
    }, [rangeKey, categoriesKey, companiesKey]);

    // Manually-scheduled (not the shared useDebouncedCallback hook) so clearing the search box can
    // explicitly cancel any still-pending timer — otherwise a stale fetch for the previous term
    // could fire after the box was cleared and silently repopulate `matches` in the background.
    const matchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    useEffect(() => {
        if (matchTimeoutRef.current) {
            clearTimeout(matchTimeoutRef.current);
            matchTimeoutRef.current = null;
        }
        if (!searchTerm) {
            setMatches({});
            return;
        }
        if (!loggedIn) return;
        matchTimeoutRef.current = setTimeout(() => runSearchMatches(searchTerm), MATCH_DEBOUNCE_MS);
        return () => {
            if (matchTimeoutRef.current) clearTimeout(matchTimeoutRef.current);
        };
    }, [searchTerm, loggedIn, rangeKey, categoriesKey, companiesKey, runSearchMatches]);

    if (loading) {
        return (
            <div className="po-incidents">
                <TabSkeleton />
            </div>
        );
    }

    if (error) {
        return (
            <div className="po-incidents">
                <TabError />
            </div>
        );
    }

    // While searching, show the best-matching categories first; counts/share/bars stay untouched —
    // only the row order changes. No search term = original (incident-count) order, unchanged.
    const displayIncidentCategories = searchTerm
        ? [...incidentCategories].sort((a, b) => {
              const aKey = a.parent && a.child ? `${a.parent}/${a.child}` : null;
              const bKey = b.parent && b.child ? `${b.parent}/${b.child}` : null;
              const aMatches = aKey ? matches[aKey] ?? 0 : 0;
              const bMatches = bKey ? matches[bKey] ?? 0 : 0;
              return bMatches - aMatches;
          })
        : incidentCategories;

    return (
        <div className="po-incidents">
            <div className="po-section-head">
                <div>
                    <h2>{tp("title")}</h2>
                    <p className="po-section-head__sub">
                        {kpis.incidentsRaised} {tp("incidents")} · {incidentCategories.length} {tc("categories")} · {tc(isCustomPeriod ? "for" : "last")} {periodDays} {tc(periodDays === 1 ? "day" : "days")}
                    </p>
                </div>
                <div className="po-search">
                    <EonUiIcon name="search" size="small" className="po-search__icon" />
                    <input
                        type="search"
                        value={searchValue}
                        onChange={(e) => setSearchValue(e.target.value)}
                        placeholder={tp("searchPlaceholder")}
                    />
                </div>
            </div>

            {/* Category breakdown table */}
            <div className="po-card po-table-card">
                <div className="po-card__head">
                    <h3>{tp("breakdownTitle")}</h3>
                    <span className="po-card__meta">{incidentCategories.length} {tc("categories")}</span>
                </div>
                <table className="po-table">
                    <thead>
                        <tr>
                            <th>{tp("thCategory")}</th>
                            <th className="num">{tp("thIncidents")}</th>
                            <th className="num">{tp("thShare")}</th>
                            <th>{tp("thDistribution")}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {displayIncidentCategories.map((c, i) => {
                            const matchKey = c.parent && c.child ? `${c.parent}/${c.child}` : null;
                            const matchCount = matchKey ? matches[matchKey] ?? 0 : 0;
                            return (
                                <tr
                                    key={c.parent && c.child ? `${c.parent}/${c.child}` : `${c.category}-${i}`}
                                    className={c.parent && c.child ? "po-row--clickable" : ""}
                                    onClick={() => openCategory(c)}
                                >
                                    <td className="po-table__strong">
                                        {c.category}{" "}
                                        {searchTerm && matchCount > 0 && (
                                            <span className="po-badge po-badge--match">
                                                {matchCount} {tp(matchCount === 1 ? "matchSuffix" : "matchesSuffix")}
                                            </span>
                                        )}
                                    </td>
                                    <td className="num">{fmt(c.incidents)}</td>
                                    <td className="num po-table__muted">{c.share}%</td>
                                    <td>
                                        <div className="po-minibar">
                                            <div className="po-minibar__fill" style={{ width: `${(c.incidents / maxIncidents) * 100}%` }} />
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                        {incidentCategories.length === 0 && (
                            <tr>
                                <td colSpan={4} className="po-table__muted">{tp("empty")}</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {/* Drill-down (popup): individual incidents for the clicked category */}
            {selectedCat && (
                <IncidentCategoryPanel
                    parent={selectedCat.parent}
                    child={selectedCat.child}
                    label={selectedCat.label}
                    range={range}
                    companies={companyFilter}
                    search={searchTerm || undefined}
                    onClose={() => setSelectedCat(null)}
                />
            )}
        </div>
    );
};

export default IncidentsTab;
